import type { ContentCardItem } from '@content-search/domain';
import { useState } from 'react';

import * as imageStyles from './ContentCardImage.css';

interface FullSizeImageDisplayProps {
  item: ContentCardItem;
  isContentBlurred: boolean;
  onToggleBlur?: () => void;
}

/**
 * Image error display component
 */
function ImageError({ contentUrl }: { contentUrl: string | undefined }) {
  return (
    <div className={imageStyles.fullImageSection}>
      <h4 className={imageStyles.imageContentTitle}>Image Content</h4>
      <div className={imageStyles.fullImageErrorContainer}>
        <p className={imageStyles.fullImageErrorText}>Unable to display image preview. Ensure URL is valid. CORS restrictions may prevent preview.</p>
        <p className={imageStyles.fullImageUrlText}>{contentUrl}</p>
      </div>
    </div>
  );
}

/**
 * Full-size image display component for expanded section
 */
// eslint-disable-next-line complexity
export function FullSizeImageDisplay({ item, isContentBlurred, onToggleBlur }: FullSizeImageDisplayProps) {
  const [imageError, setImageError] = useState(false);
  const hasImage = Boolean(item.contentUrl) && item.contentType?.toLowerCase() === 'image';

  if (!hasImage) {
    return null;
  }

  const isSafeContent = item.classificationResult !== 'rejected';

  if (imageError) {
    return <ImageError contentUrl={item.contentUrl} />;
  }

  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleBlur) {
      onToggleBlur();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onToggleBlur && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onToggleBlur();
    }
  };

  return (
    <div className={imageStyles.fullImageSection}>
      <h4 className={imageStyles.imageContentTitle}>Image Content</h4>
      <div
        className={imageStyles.fullImageContainer}
        role="button"
        style={{ cursor: onToggleBlur ? 'pointer' : 'default' }}
        tabIndex={onToggleBlur ? 0 : undefined}
        title={onToggleBlur && isContentBlurred ? 'Click to show content' : onToggleBlur ? 'Click to hide content' : undefined}
        onClick={handleImageClick}
        onKeyDown={handleKeyDown}
      >
        <img
          alt={`Content for ${item.subjectId}`}
          className={`${imageStyles.fullImage} ${!isSafeContent && isContentBlurred ? imageStyles.blurredFullImage : ''}`}
          src={item.contentUrl || ''}
          onError={() => setImageError(true)}
        />
      </div>
    </div>
  );
}
